export const useDetailProductPage = async () => {
  const nuxtApp = useNuxtApp()
  const {
    item: product,
    relatedItems: relateProducts,
    breadcrumb: breadcrumbItem,
  } = await usePageDetailApi<Product>({
    path: '/products/',
    previewPath: '/product-previews/',
    query: { populate: 'productCategory' },
  })

  // Update view count (once per session)
  if (import.meta.client && product.value?._id) {
    const viewKey = `viewed-product-${product.value._id}`
    if (!sessionStorage.getItem(viewKey)) {
      const { $api } = nuxtApp
      $api(`/products/${product.value._id}/view`, { method: 'PATCH' })
        .then(() => sessionStorage.setItem(viewKey, '1'))
        .catch(() => {})
    }
  }

  useCustomSeo({
    title: () => product.value?.metaTitleFront,
    description: () => product.value?.metaDescriptionFront || product.value?.description,
    image: () => product.value?.ogImage?.large || product.value?.imageCover?.large,
    keywords: () => product.value?.focusKeyword,
  })

  const { getPath, slugPageInfo, domain } = useNavigationMenu()
  const { analyticEvent, fbPixelEvent } = useAnalytic()

  const eventProductRelated = (title: string) => {
    if (slugPageInfo.value && product.value) {
      analyticEvent({
        ga4ActionName: 'click_product_related',
        ga4Label: title,
      })
      fbPixelEvent({
        fbCategory: 'trackCustom',
        fbEvent: 'Product / Service - Releated',
        fbValue: { label: title },
      })
    }
  }

  const categoryEventName = 'Product / Service'
  const eventShareName = 'click_product_social_shared'
  const eventButtonName = 'click_product_cta'

  const { adaptIcon } = useIconV2Adapter()

  // org schema for detail product page
  const { currentLocaleCode } = useLanguage()
  const currencyStore = useCurrencyStore()
  const currencyName = computed(() => currencyStore.currentName)
  if (product.value) {
    const p = product.value
    const defaultValidUntil = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
    const productUrl = `${domain.value}${getPath({ componentName: 'Product', slug: p.slug })}`
    nuxtApp.runWithContext(() => useSchemaOrg([
      defineProduct({
        '@id': `${domain.value}/${p.slug || ''}#product`,
        name: p.metaTitleFront || p.title || '',
        description: p.metaDescriptionFront || p.shortDescription || '',
        sku: p._id || '',
        category: p.productCategory?.title || undefined,
        brand: { '@id': `${domain.value}/#organization` },
        image: p.ogImage?.large || p.imageCover?.large || '',
        url: productUrl,
        inLanguage: currentLocaleCode.value,
        itemCondition: 'https://schema.org/NewCondition',
        offers: p.priceOfDiscounted && p.priceOfDiscounted < p.price
          ? {
              '@type': 'AggregateOffer',
              lowPrice: p.priceOfDiscounted || 0,
              highPrice: p.price || 0,
              priceCurrency: currencyName.value,
              availability: 'https://schema.org/InStock',
              url: productUrl,
              priceValidUntil: defaultValidUntil,
            }
          : {
              '@type': 'Offer',
              price: p.price || 0,
              priceCurrency: currencyName.value,
              availability: 'https://schema.org/InStock',
              url: productUrl,
              priceValidUntil: defaultValidUntil,
            },
      }),
    ]))
  }

  return {
    menu: slugPageInfo,
    product: {
      ...product.value,
      callToActionIcon: adaptIcon(product.value?.callToActionIcon || ''),
    },
    relateProducts,
    breadcrumbItem,
    getPath,
    eventProductRelated,
    categoryEventName,
    eventShareName,
    eventButtonName,
  }
}
